import React from 'react';
import { useNavigate } from 'react-router-dom';
import { trackABTestEvent } from '../../utils/abTest';
import useDemoPlayer, { typingDuration, useStepCount, useTypedText } from '../demo/useDemoPlayer';
import { trackDemoCta } from '../demo/demoTracking';
import HwpDocPane, { HWP_DEMO_FORM_CELLS } from '../demo/HwpDocPane';
import './HwpDemoSection.css';

// AI 한글 데모. 왼쪽은 /chat 대화, 오른쪽은 실제 한글 편집 패널(메뉴·도구 막대·문서·저장 버튼)을 따른다.
const HWP_DEMO_USER_MSG = '4학년 봄소풍 가정통신문으로 채워 주세요.\n5월 15일 경주, 준비물은 도시락·물·간식, 동의서는 5월 11일까지요.';
const HWP_DEMO_AI_REPLY = '양식 그대로 칸을 채웠어요. 오른쪽에서 바로 고치고 한글 파일로 저장하세요.';

const FILL_TICK_MS = 220;

// 0 요청 입력 → 1 작업 중 → 2 답변 + 칸 채우기 → 3(완성) 저장 버튼 활성
const STEPS = [
  typingDuration(HWP_DEMO_USER_MSG, 1, 28),
  1300,
  HWP_DEMO_FORM_CELLS.length * FILL_TICK_MS + 600,
];

function HwpDemoSection({
  trackingLocation = 'demo_hwp',
  surface = 'home',
  ctaPath = '/chat?category=document',
  ctaLabel = 'AI 한글 작성하러 가기 →',
  headingTitle,
  headingDesc = '학교 양식을 올리고 말로 부탁하면 칸을 채워요. 화면에서 바로 고치고 한글 파일로 저장해요.',
  className = '',
  style = {},
}) {
  const navigate = useNavigate();
  const player = useDemoPlayer({ steps: STEPS, surface, feature: 'hwp', threshold: 0.45 });
  const { phase, done, paused } = player;
  const userMsg = useTypedText(HWP_DEMO_USER_MSG, { active: phase === 0, complete: phase > 0, paused, charsPerTick: 1, tickMs: 28 });
  const aiReply = useTypedText(HWP_DEMO_AI_REPLY, { active: phase === 2, complete: phase > 2, paused, charsPerTick: 2, tickMs: 22 });
  const filledCount = useStepCount(HWP_DEMO_FORM_CELLS.length, { active: phase === 2, complete: phase > 2, paused, tickMs: FILL_TICK_MS });

  const handleCtaClick = () => {
    trackABTestEvent('landing_cta_button', 'cta_click', { location: trackingLocation });
    trackDemoCta(surface, 'hwp', ctaPath);
    navigate(ctaPath);
  };

  const controlLabel = done ? '다시 보기' : player.paused ? '이어 보기' : '멈추기';

  const titleNode = headingTitle ?? (
    <>
      학교 양식 그대로,
      <br />
      AI가 채워드려요
    </>
  );

  return (
    <section
      ref={player.ref}
      className={`hwp-demo-section ${className}`.trim()}
      style={style}
    >
      <div className="hwp-demo-heading">
        <div className="hwp-demo-heading-title">{titleNode}</div>
        <p className="hwp-demo-heading-desc">{headingDesc}</p>
      </div>

      <figure className="hwp-demo-mockup" aria-label="AI 한글 예시 화면">
        <div className="hwp-demo-mockup-bar">
          <span className="hwp-demo-mockup-title">AI 한글</span>
          {!player.reduced && (
            <button
              type="button"
              className="hwp-demo-control"
              onClick={() => (done ? player.replay() : player.togglePause())}
            >
              {controlLabel}
            </button>
          )}
        </div>

        <div className="hwp-demo-split">
          <div className="hwp-demo-chat-side">
            <div className="hwp-demo-attached-chip">
              <span className="hwp-demo-attached-icon">한</span>
              <span>가정통신문_양식.hwpx</span>
            </div>
            <div className="hwp-demo-msg-user">
              {userMsg}
              {phase === 0 && <span className="hwp-demo-cursor" />}
            </div>

            {phase === 1 && (
              <div className="hwp-demo-thinking-row">
                <div className="hwp-demo-msg-ai-avatar">왓</div>
                <div className="hwp-demo-thinking-bubble">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            {phase >= 2 && (
              <div className="hwp-demo-msg-ai-row">
                <div className="hwp-demo-msg-ai-avatar">왓</div>
                <div className="hwp-demo-msg-ai">
                  {aiReply}
                  {phase === 2 && aiReply.length < HWP_DEMO_AI_REPLY.length && (
                    <span className="hwp-demo-cursor" />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="hwp-demo-form-side">
            <HwpDocPane filledCount={filledCount} filling={phase === 2} done={done} />
          </div>
        </div>
      </figure>

      <div className="hwp-demo-cta-wrap show">
        <button type="button" className="hwp-demo-cta-btn" onClick={handleCtaClick}>
          {ctaLabel}
        </button>
      </div>
    </section>
  );
}

export default HwpDemoSection;
